import { motion } from 'framer-motion';
import { useAppDataContext } from '../context/AppDataContext';
import { useWeeklyJourney, type JourneyWeek } from '../hooks/useWeeklyJourney';
import WeekBridge from '../components/bridge/WeekBridge';

const fmt = (d: Date) => `${d.getDate()}.${d.getMonth() + 1}`;
const range = (w: JourneyWeek) => {
  const end = new Date(w.start);
  end.setDate(end.getDate() + 6);
  return `${fmt(w.start)}–${fmt(end)}`;
};
const planksText = (n: number) => (n === 1 ? 'קרש אחד' : `${n} קרשים`);

// Said about the week so far, never about what is missing from it.
const weekLine = (laid: number, touched: number) => {
  if (laid >= 5) return 'שבוע של גשר כמעט שלם. רואים את זה.';
  if (laid >= 1) return 'כל קרש מחזיק. ממשיכים לאט, בקצב שלך.';
  if (touched >= 1) return 'עצרת לספר איך מרגיש. גם זה צעד על הגשר.';
  return 'הגשר מחכה. אפשר להניח קרש ראשון מתי שמתאים.';
};

/** Instead of a streak that breaks: one bridge per week, and every past bridge stays standing. */
export default function JourneyPage() {
  const { profile } = useAppDataContext();
  const weeks = useWeeklyJourney(profile?.id);
  const avatarId = localStorage.getItem('remind_avatar') || profile?.avatar_id || 'default';

  if (!profile || !weeks) {
    return <div className="flex items-center justify-center h-full text-muted-foreground bg-background">טוען...</div>;
  }

  const [current, ...past] = weeks;
  const touched = current.planks.filter((p) => p === 'touched').length;
  const total = weeks.reduce((n, w) => n + w.laid, 0);
  const today = new Date().getDay();

  return (
    <div className="h-full overflow-y-auto bg-background dir-rtl">
      <div className="px-5 pt-6 pb-28 flex flex-col gap-6">
        <header>
          <h1 className="font-hand text-5xl leading-none text-foreground">הגשרים שלי</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            כל שבוע הוא גשר אחד, וכל משימה שעשית היא קרש בו. ימים בלי קרש הם רק רווח, לא נפילה.
          </p>
        </header>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-bridge/40 bg-bridge-soft/60 p-4 shadow-sm"
          aria-label="השבוע הזה"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-lg font-extrabold text-bridge-foreground">השבוע הזה</h2>
            <span className="text-xs font-bold text-bridge-foreground/70">{range(current)}</span>
          </div>
          <WeekBridge
            planks={current.planks}
            avatarId={avatarId}
            today={today}
            labels
            className="block w-full mt-1"
            title={`השבוע: ${planksText(current.laid)}`}
          />
          <p className="mt-1 text-sm font-bold text-bridge-foreground">
            {current.laid > 0 ? `הנחת ${planksText(current.laid)}.` : 'עוד לא הונחו קרשים השבוע.'} {weekLine(current.laid, touched)}
          </p>
        </motion.section>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-card border border-border p-3 text-center">
            <div className="text-3xl font-extrabold text-foreground">{weeks.length}</div>
            <div className="text-xs text-muted-foreground">{weeks.length === 1 ? 'גשר מאז שהתחלת' : 'גשרים מאז שהתחלת'}</div>
          </div>
          <div className="rounded-2xl bg-card border border-border p-3 text-center">
            <div className="text-3xl font-extrabold text-foreground">{total}</div>
            <div className="text-xs text-muted-foreground">קרשים בסך הכל</div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground" aria-hidden="true">
          <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-bridge" />משימה שנעשתה</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-bridge-soft border border-bridge" />רק עצירה לספר איך מרגיש</span>
        </div>

        {past.length > 0 && (
          <section aria-label="השבועות הקודמים" className="flex flex-col gap-1">
            <h2 className="text-sm font-bold text-muted-foreground mb-1">הגשרים שכבר עומדים</h2>
            {past.map((w, i) => (
              <motion.div
                key={w.start.toISOString()}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.08 * i }}
                className="flex items-center gap-3"
                style={{ opacity: Math.max(0.55, 1 - i * 0.08) }}
              >
                <WeekBridge planks={w.planks} className="flex-1 min-w-0" title={`${range(w)}: ${planksText(w.laid)}`} />
                <div className="w-16 shrink-0 text-left">
                  <div className="text-xs font-bold text-foreground">{planksText(w.laid)}</div>
                  <div className="text-[11px] text-muted-foreground">{range(w)}</div>
                </div>
              </motion.div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
